/**
 * useSEO — Dynamic SEO meta tag hook
 * =====================================
 * Fetches SEO data from /api/public/seo/{path} and applies via Helmet.
 * Falls back to provided defaults if the CMS has no entry for the page.
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API = typeof window !== 'undefined' ? window.location.origin : '';
const seoCache = {};

export function useSEO(defaults = {}) {
  const location = useLocation();
  const path = location.pathname;
  const [seo, setSeo] = useState({ ...defaults, loaded: false });

  useEffect(() => {
    // Never send token-bearing / private paths to the SEO endpoint (they would
    // leak the secret token into logs). These pages are not indexable anyway.
    const PRIVATE_PREFIXES = ['/intake/', '/consent', '/reset-password', '/accept-invite'];
    if (PRIVATE_PREFIXES.some((p) => path.startsWith(p))) {
      setSeo({ ...defaults, robots: 'noindex, nofollow', loaded: true });
      return;
    }
    if (seoCache[path]) {
      setSeo({ ...defaults, ...seoCache[path], loaded: true });
      return;
    }

    let cancelled = false;
    fetch(`${API}/api/public/seo${path}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data && data.found) {
          const merged = {
            title: data.title || defaults.title || '',
            description: data.meta_description || defaults.description || '',
            ogTitle: data.og_title || data.title || defaults.title || '',
            ogDescription: data.og_description || data.meta_description || defaults.description || '',
            ogImage: data.og_image || defaults.ogImage || '',
            canonical: data.canonical_url || defaults.canonical || '',
            robots: data.robots || 'index, follow',
            keywords: (data.keywords || []).join(', '),
            structuredData: data.structured_data || null,
            loaded: true,
          };
          seoCache[path] = merged;
          setSeo(merged);
        } else {
          setSeo({ ...defaults, loaded: true });
        }
      })
      .catch(() => {
        if (!cancelled) setSeo({ ...defaults, loaded: true });
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [path]);

  return seo;
}
